import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Edit3,
  Search,
  Building2,
  Phone,
  User,
  Clock,
  Copy,
  Power,
  Truck,
  Loader2,
  QrCode,
  AlertTriangle,
} from "lucide-react";
import CompanyFormModal from "./CompanyFormModal";
import companyService from "@/services/companyService";
import { showError, showInfo } from "@/store/errorStore";
import { useAuth } from "@/hooks/useAuth";
import { isOwner } from "@/lib/permissions";

/**
 * Erros HTTP já são exibidos pelo modal global (axiosInterceptor);
 * só mostramos aqui erros sem resposta do servidor (ex.: falha local).
 */
const showLocalError = (error, fallback) => {
  if (!error?.response && !error?.request) {
    showError(error?.message || fallback, "Erro");
  }
};

const StatCard = ({ label, value, className }) => (
  <Card>
    <CardContent className="p-4">
      <p className="text-sm text-gray-500">{label}</p>
      <p className={`text-2xl font-bold ${className || "text-gray-900"}`}>
        {value}
      </p>
    </CardContent>
  </Card>
);

/**
 * Gestão de empresas: cadastro, edição, abrir/fechar loja e delivery.
 * - ROLE_OWNER: todas as empresas e "Nova Empresa" (POST /companies é só owner).
 * - Demais perfis: a API devolve só a própria empresa; a tela vira
 *   "Minha Empresa" (dados, abrir/fechar a loja e delivery).
 */
export default function CompaniesManagement() {
  const { user, syncCompany } = useAuth();
  const ownerView = isOwner(user);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingCode, setLoadingCode] = useState(null);
  // Toggle em andamento: `${code}:availability` ou `${code}:delivery`
  const [pendingToggle, setPendingToggle] = useState(null);
  const [closingCompany, setClosingCompany] = useState(null);

  useEffect(() => {
    fetchCompanies();
  }, []);

  /**
   * Buscar todas as empresas
   */
  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const data = await companyService.getAllCompanies();
      setCompanies(Array.isArray(data) ? data : []);
    } catch (error) {
      showLocalError(error, "Erro ao carregar empresas");
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Substitui a empresa na lista pela versão devolvida pela API
   */
  const replaceCompany = (updated) => {
    if (!updated?.code) return;
    setCompanies((prev) =>
      prev.map((c) => (c.code === updated.code ? { ...c, ...updated } : c)),
    );
    // Header/Dashboard exibem o status da loja da empresa em foco
    syncCompany(updated);
  };

  const handleCreate = () => {
    setEditingCompany(null);
    setShowModal(true);
  };

  /**
   * Abrir modal de edição (busca dados atualizados pelo código)
   */
  const handleEdit = async (company) => {
    setLoadingCode(company.code);
    try {
      const fresh = await companyService.getCompanyByCode(company.code);
      setEditingCompany(fresh || company);
      setShowModal(true);
    } catch (error) {
      showLocalError(error, "Erro ao carregar empresa");
    } finally {
      setLoadingCode(null);
    }
  };

  const handleSave = async (companyData) => {
    setSaving(true);
    try {
      if (editingCompany) {
        const updated = await companyService.updateCompany(
          editingCompany.code,
          companyData,
        );
        replaceCompany(updated);
        showInfo(
          `Empresa "${companyData.name}" atualizada com sucesso`,
          "Empresa Atualizada",
        );
      } else {
        await companyService.createCompany(companyData);
        showInfo(
          `Empresa "${companyData.name}" criada com sucesso`,
          "Empresa Criada",
        );
        await fetchCompanies();
      }
      setShowModal(false);
      setEditingCompany(null);
    } catch (error) {
      showLocalError(error, "Erro ao salvar empresa");
    } finally {
      setSaving(false);
    }
  };

  /**
   * Ligar/desligar sistema. Fechar a loja pede confirmação.
   */
  const handleAvailabilityChange = (company, available) => {
    if (!available) {
      setClosingCompany(company);
      return;
    }
    saveAvailability(company, true);
  };

  const saveAvailability = async (company, available) => {
    setPendingToggle(`${company.code}:availability`);
    try {
      const updated = await companyService.setAvailability(
        company.code,
        available,
      );
      replaceCompany(updated || { ...company, available });
      showInfo(
        `Loja "${company.name}" ${available ? "aberta" : "fechada"} com sucesso`,
        available ? "Loja Aberta" : "Loja Fechada",
      );
    } catch (error) {
      showLocalError(error, "Erro ao alterar disponibilidade");
    } finally {
      setPendingToggle(null);
    }
  };

  const handleDeliveryChange = async (company, delivery) => {
    setPendingToggle(`${company.code}:delivery`);
    try {
      const updated = await companyService.setDelivery(company.code, delivery);
      replaceCompany(updated || { ...company, delivery });
      showInfo(
        `Delivery ${delivery ? "ativado" : "desativado"} para "${company.name}"`,
        "Delivery",
      );
    } catch (error) {
      showLocalError(error, "Erro ao alterar delivery");
    } finally {
      setPendingToggle(null);
    }
  };

  const handleCopyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      showInfo("Código copiado para a área de transferência", "Copiado");
    } catch {
      showError("Não foi possível copiar o código", "Erro");
    }
  };

  const filteredCompanies = companies.filter((company) => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return true;
    return [
      company.name,
      company.contactName,
      company.contactPhone,
      company.code,
    ]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(query));
  });

  const openCount = companies.filter((c) => c.available).length;
  const deliveryCount = companies.filter(
    (c) => c.available && c.delivery,
  ).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {ownerView ? "Gestão de Empresas" : "Minha Empresa"}
          </h1>
          <p className="text-gray-500 mt-1">
            {ownerView
              ? "Cadastro, funcionamento e delivery de cada empresa"
              : "Dados da loja, funcionamento e delivery"}
          </p>
        </div>
        {ownerView && (
          <Button
            onClick={handleCreate}
            className="bg-[#1b5e20] hover:bg-[#144919] text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nova Empresa
          </Button>
        )}
      </div>

      {ownerView && (
      <>
      {/* Estatísticas */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Empresas cadastradas" value={companies.length} />
        <StatCard
          label="Lojas abertas"
          value={openCount}
          className="text-green-600"
        />
        <StatCard
          label="Com delivery ativo"
          value={deliveryCount}
          className="text-blue-600"
        />
      </div>

      {/* Busca */}
      <Card>
        <CardHeader>
          <CardTitle>Buscar Empresas</CardTitle>
          <CardDescription>
            Pesquise por nome, contato, telefone ou código
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              placeholder="Digite para buscar..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>
      </>
      )}

      {/* Lista */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p className="text-gray-600">Carregando empresas...</p>
          </div>
        </div>
      ) : filteredCompanies.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Building2 className="w-12 h-12 text-gray-400 mb-3" />
          <p className="text-gray-600 font-medium">
            {searchQuery
              ? "Nenhuma empresa encontrada"
              : "Nenhuma empresa cadastrada"}
          </p>
          <p className="text-gray-500 text-sm mt-1">
            {searchQuery
              ? "Tente outro termo de busca"
              : ownerView
                ? "Clique em 'Nova Empresa' para começar"
                : "Seu usuário não está vinculado a uma empresa"}
          </p>
        </div>
      ) : (
        <div
          className={
            ownerView
              ? "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
              : "grid grid-cols-1 max-w-xl gap-6"
          }
        >
          {filteredCompanies.map((company) => {
            const availabilityPending =
              pendingToggle === `${company.code}:availability`;
            const deliveryPending =
              pendingToggle === `${company.code}:delivery`;
            return (
              <Card
                key={company.code}
                className={`flex flex-col overflow-hidden transition-shadow hover:shadow-lg ${
                  company.available ? "" : "bg-gray-50"
                }`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start gap-3">
                    {company.logoUrl ? (
                      <img
                        src={company.logoUrl}
                        alt={company.name}
                        className="w-14 h-14 object-contain rounded-lg border bg-white flex-shrink-0"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg truncate">
                        {company.name}
                      </CardTitle>
                      <div className="flex flex-wrap gap-1 mt-2">
                        <Badge
                          variant="outline"
                          className={
                            company.available
                              ? "bg-green-100 text-green-800 border-green-200"
                              : "bg-gray-100 text-gray-600 border-gray-200"
                          }
                        >
                          {company.available ? "Aberta" : "Fechada"}
                        </Badge>
                        <Badge
                          variant="outline"
                          className={
                            company.delivery
                              ? "bg-blue-100 text-blue-800 border-blue-200"
                              : "bg-gray-100 text-gray-600 border-gray-200"
                          }
                        >
                          {company.delivery ? "Delivery ativo" : "Sem delivery"}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="flex-1 flex flex-col gap-4">
                  {company.description && (
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {company.description}
                    </p>
                  )}

                  {(company.contactName || company.contactPhone) && (
                    <div className="space-y-1 text-sm text-gray-700">
                      {company.contactName && (
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-500" />
                          <span>{company.contactName}</span>
                        </div>
                      )}
                      {company.contactPhone && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-gray-500" />
                          <span>
                            {companyService.formatPhone(company.contactPhone)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {companyService.isPixConfigured(company) ? (
                    <div className="flex items-start gap-2 text-sm text-gray-700">
                      <QrCode className="h-4 w-4 mt-0.5 text-gray-500 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="truncate" title={company.pixKey}>
                          PIX: {company.pixKey}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {company.pixMerchantName || company.name} ·{" "}
                          {company.pixMerchantCity}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 p-3 rounded-md">
                      <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                      <span>
                        PIX indisponível: falta cadastrar a chave PIX e a
                        cidade do recebedor. Edite a empresa para informar.
                      </span>
                    </div>
                  )}

                  {(company.businessHoursWeekdays ||
                    company.businessHoursWeekend) && (
                    <div className="flex items-start gap-2 text-xs text-gray-600 bg-gray-50 p-3 rounded-md">
                      <Clock className="h-3 w-3 mt-0.5 flex-shrink-0" />
                      <div className="space-y-1">
                        {company.businessHoursWeekdays && (
                          <p>{company.businessHoursWeekdays}</p>
                        )}
                        {company.businessHoursWeekend && (
                          <p>{company.businessHoursWeekend}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Controles de funcionamento */}
                  <div className="mt-auto space-y-3 border-t pt-4">
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <Power className="h-4 w-4" />
                        Loja aberta
                      </span>
                      {availabilityPending ? (
                        <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
                      ) : (
                        <Switch
                          checked={!!company.available}
                          onCheckedChange={(checked) =>
                            handleAvailabilityChange(company, checked)
                          }
                          disabled={pendingToggle !== null}
                          aria-label={`Loja ${company.name} aberta`}
                        />
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <Truck className="h-4 w-4" />
                        Delivery
                      </span>
                      {deliveryPending ? (
                        <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
                      ) : (
                        <Switch
                          checked={!!company.delivery}
                          onCheckedChange={(checked) =>
                            handleDeliveryChange(company, checked)
                          }
                          disabled={pendingToggle !== null}
                          aria-label={`Delivery da empresa ${company.name}`}
                        />
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-2 border-t pt-3">
                    <button
                      type="button"
                      onClick={() => handleCopyCode(company.code)}
                      className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 font-mono truncate"
                      title="Copiar código da empresa"
                    >
                      <Copy className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">{company.code}</span>
                    </button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(company)}
                      disabled={loadingCode === company.code}
                    >
                      {loadingCode === company.code ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <Edit3 className="w-4 h-4 mr-1" />
                      )}
                      Editar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <CompanyFormModal
        open={showModal}
        onOpenChange={setShowModal}
        company={editingCompany}
        onSave={handleSave}
        loading={saving}
      />

      {/* Confirmação ao fechar loja */}
      <AlertDialog
        open={!!closingCompany}
        onOpenChange={(open) => !open && setClosingCompany(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Fechar loja?</AlertDialogTitle>
            <AlertDialogDescription>
              A loja <strong>{closingCompany?.name}</strong> deixará de receber
              pedidos até ser aberta novamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                saveAvailability(closingCompany, false);
                setClosingCompany(null);
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Fechar loja
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
